import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function About() {
  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="flex-shrink-0 w-full lg:w-[360px]">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/cdd408e2791880a2b3e38af7d959fee9.png" alt="Carol L Berry, Real Estate Professional" className="w-full h-full object-cover object-top" />
              </div>
            </div>
            <div className="flex-1">
              <p className="text-primary-500 font-body text-sm tracking-widest uppercase mb-3">About Carol</p>
              <h2 className="font-heading text-foreground-900 text-3xl md:text-4xl lg:text-5xl mb-5">
                Carol L Berry
              </h2>
              <p className="text-foreground-500 font-body text-base mb-1">Real Estate Professional</p>
              <p className="text-foreground-500 font-body text-sm mb-6">Century 21 Cedarcrest Realty, Inc.</p>
              <div className="space-y-4 text-foreground-600 font-body text-sm md:text-base leading-relaxed">
                <p>
                  Carol L Berry is a dedicated real estate professional who brings years of experience helping
                  homeowners navigate the selling process with confidence. She understands that selling a home
                  is one of the most significant financial decisions most people make — and she treats every
                  client's situation with the attention and care it deserves.
                </p>
                <p>
                  Her approach combines deep local market knowledge with skilled negotiation and clear,
                  consistent communication. From the first conversation to the closing table, Carol is
                  committed to making sure her clients feel informed, supported and confident in every decision.
                </p>
              </div>
              <div className="mt-7 flex flex-col sm:flex-row gap-4">
                <Link to="/home-valuation#valuation-form" className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
                  <i className="ri-home-4-line" />Request a Home Valuation
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-foreground-300 text-foreground-700 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-foreground-50 transition-all">
                  <i className="ri-chat-3-line" />Contact Carol
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Real Estate */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 lg:px-10">
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl text-center mb-10">
            Why Carol Works in Real Estate
          </h2>
          <div className="space-y-5 text-foreground-600 font-body text-sm md:text-base leading-relaxed">
            <p>
              Real estate is about more than property — it is about people. Carol entered the industry because
              she genuinely enjoys helping individuals and families navigate one of the most important
              transitions in their lives.
            </p>
            <p>
              She knows that every homeowner's situation is unique. Whether someone is upsizing for a growing
              family, downsizing after the children have moved out, relocating for a new opportunity or
              handling an inherited property, Carol takes the time to understand what matters most to each
              person she works with.
            </p>
            <p>
              That personal attention — combined with her knowledge of the local market, her negotiation
              ability and her commitment to clear communication — is what her clients consistently tell her
              they appreciate most.
            </p>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl text-center mb-12">
            Carol's Approach to Helping Sellers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: 'ri-user-heart-line', title: 'Personal Service', desc: 'Carol treats every client as an individual. She takes the time to understand your goals, timeline and concerns before offering any advice or recommendations.' },
              { icon: 'ri-bar-chart-box-line', title: 'Market Knowledge', desc: 'She stays current with local market conditions, recent sales and buyer trends so you can make decisions based on accurate, up-to-date information.' },
              { icon: 'ri-chat-check-line', title: 'Clear Communication', desc: 'You will never be left wondering what happens next. Carol keeps you informed at every stage and is always available to answer your questions.' },
            ].map((item, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 mx-auto mb-4">
                  <i className={`${item.icon} text-primary-500 text-xl`} />
                </div>
                <h3 className="font-heading text-foreground-900 text-lg mb-3">{item.title}</h3>
                <p className="text-foreground-600 font-body text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliation */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl mb-5">
            Backed by Century 21 Cedarcrest Realty, Inc.
          </h2>
          <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed mb-6">
            Carol is proud to be affiliated with Century 21 Cedarcrest Realty, Inc., one of the most recognised names in real estate.
            This affiliation gives her clients access to extensive resources, marketing tools and a broad
            professional network — while Carol herself remains the personal point of contact throughout
            the entire process.
          </p>
          <a href="http://carolberry.C21.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary-600 font-body text-sm font-semibold hover:text-primary-700 transition-colors">
            Visit Carol's Century 21 Cedarcrest Realty Page
            <i className="ri-arrow-right-up-line" />
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-foreground-950">
        <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <h2 className="font-heading text-accent-100 text-2xl md:text-3xl mb-4">
            Let's Have a Conversation
          </h2>
          <p className="text-background-100/70 font-body text-base mb-8">
            Whether you have questions about the market, your property's value or the selling process,
            Carol would be happy to talk. No pressure — just helpful guidance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/home-valuation#valuation-form" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
              <i className="ri-home-4-line text-lg" />Request a Home Valuation
            </Link>
            <Link to="/contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-accent-300 text-accent-100 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-accent-300/10 transition-all">
              <i className="ri-chat-3-line text-lg" />Contact Carol
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}